// The browser's side of the same-origin /api/* Worker (Block I).
//
// Same origin is why this file is short: no base URL, no CORS, no preflight —
// fractalart.gallery serves the app and its API from one Worker, so a relative
// path is the whole client.

// THE BACKEND SWITCH.
//
// Without an Auth0 `audience`, Auth0 hands the SPA an OPAQUE access token, not a
// JWT — the Worker cannot verify it, and every authenticated call would 401. So a
// build with no audience is a build with no usable backend, and it says so here
// rather than discovering it one failed save at a time. When this is false the
// app stays exactly as it shipped in Block H: anonymous, localStorage-only, for
// everyone including logged-in users.
//
// CRA inlines this at BUILD time, so it is set in Workers Builds' *Build
// variables* box (Block I, sharp edge 1) — not in the runtime variables.
export const apiEnabled = Boolean(process.env.REACT_APP_AUTH0_AUDIENCE);

export class ApiError extends Error {
    constructor(status, code, detail) {
        super(`api ${status}${code ? ` (${code})` : ''}`);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.detail = detail;
    }
}

const request = async (path, { method = 'GET', token, body } = {}) => {
    const headers = { accept: 'application/json' };
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';

    const response = await fetch(path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
    });

    // The API always answers in JSON — but a 500 from the edge, or an
    // `not_found_handling` fallback serving the SPA shell, would not. Parsing
    // defensively keeps "the server returned HTML" from surfacing as a syntax
    // error thrown from inside a save.
    let payload = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }

    if (!response.ok) {
        throw new ApiError(response.status, payload?.error, payload?.reason);
    }
    return payload;
};

/** GET /api/health — no auth, no database; proves the Worker is routing. */
export const health = () => request('/api/health');

/** GET /api/gallery/mine — the signed-in user's gallery, newest first. */
export const fetchGallery = (token) => request('/api/gallery/mine', { token });

/**
 * POST /api/gallery — one entry, or a whole gallery in one request.
 *
 * The same call serves a single Save and the first-login migration; the server
 * is idempotent on (user, coordId), so retrying either is safe.
 */
export const saveEntries = (token, entries) =>
    request('/api/gallery', { method: 'POST', token, body: { entries } });

/** DELETE /api/gallery/:coordId */
export const deleteEntry = (token, id) =>
    request(`/api/gallery/${encodeURIComponent(id)}`, { method: 'DELETE', token });
